import React, { useState } from "react"

import { Form, Formik } from "formik"
import * as Yup from "yup"

import { AlertLevel } from "~tailwindui/types/enums"

import { AlertMessageBox } from "~tailwindui/Basics"

import { publicClient as client } from "src/urql-client"

import { gql, useMutation, useQuery } from "hooks/urql"

import {
  AwsFileField,
  Field,
  FormFailures,
  SubmitButton,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

const ValidationSchema = Yup.object().shape({
  selfieUrl: Yup.mixed().test(
    "selfieUrl",
    "Selfie file upload or external URL is required",
    function () {
      if (!this.parent.selfieUrl && !this.parent.externalUrl) return false
      return true
    }
  ),
})

const SelfieSubmission = ({ token }) => {
  const formState = {
    token,
    selfieUrl: "",
    selfieFilename: "",
    externalUrl: "",
  }

  const [isUploading, setIsUploading] = useState(false)
  const [uploaded, setUploaded] = useState(false)

  const [result] = useQuery({
    query: signedUrlQuery,
    variables: { token },
    client,
  })
  const [, runMutation] = useMutation(submitSelfieMutation, client)

  const handleSubmit = (values, actions) => {
    setUploaded(false)
    runMutation(values)
      .then(result => {
        if (result.error) {
          setUploaded(false)
          handleFailure(actions)
          return
        }

        const { failures } = result.data.submitTutorApplicationSelfie
        if (failures) {
          setUploaded(false)
          handleFailure(actions, failures)
        } else {
          actions.setSubmitting(false)
          actions.setStatus(null)
          setUploaded(true)
        }
      })
      .catch(() => {
        setUploaded(false)
        handleFailure(actions)
      })
  }

  return (
    <UrqlLoadingIndicator result={result}>
      {({ data }) => (
        <Formik
          initialValues={formState}
          validationSchema={ValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <p>
                To submit your video, either attach a video file or provide a
                YouTube, Vimeo, or Loom link. Please include your first and last
                name in the filename. We look forward to watching your video and
                learning more about you! Note: For those invited to move after
                the video, the last step is an interview and teaching demo.
              </p>
              <AwsFileField
                name="selfie"
                label="Selfie Video"
                signedUrl={data.tutorApplication.selfie.signedUrl}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
              />
              <div className="alert bg-blue-400 p-5 text-left italic">
                Note: uploading files takes time -- make sure you stay on this
                page until the "Uploading..." text disappears and then be sure
                to click "Submit Selfie" below!
                <br />
                <div className="text-center font-bold normal-case">
                  If everything worked, you will see a green success
                  notification at the bottom of the form.
                </div>
              </div>
              <div>
                <p>OR</p>
              </div>
              <Field
                name="externalUrl"
                label="Provide a Youtube/Loom/Vimeo link"
              />
              <FormFailures />
              {uploaded && (
                <AlertMessageBox level={AlertLevel.Success}>
                  Selfie uploaded successfully. We'll be in touch!
                </AlertMessageBox>
              )}
              <SubmitButton
                text="Submit Selfie"
                submitting={isSubmitting || isUploading}
              />
            </Form>
          )}
        </Formik>
      )}
    </UrqlLoadingIndicator>
  )
}

const signedUrlQuery = gql`
  query TutorApplicationsSelfieSubmission($token: String!) {
    tutorApplication(token: $token) {
      selfie {
        signedUrl {
          url
          key
          successActionStatus
          acl
          policy
          credential
          algorithm
          date
          signature
        }
      }
    }
  }
`

const submitSelfieMutation = gql`
  mutation (
    $token: String!
    $selfieUrl: String
    $selfieFilename: String
    $externalUrl: String
  ) {
    submitTutorApplicationSelfie(
      token: $token
      selfieUrl: $selfieUrl
      selfieFilename: $selfieFilename
      externalUrl: $externalUrl
    ) {
      failures {
        message
      }
    }
  }
`

export default SelfieSubmission
